import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AccountSwitcher from './components/AccountSwitcher';
import TransactionHistory from './components/TransactionHistory';
import PendingTransactions from './components/PendingTransactions';
import KYCStatus from './components/KYCStatus';
import PaymentMethods from './components/PaymentMethods';
import Icon from '../../components/AppIcon';

const AccountManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Mock data for accounts
  const [accounts] = useState([
    {
      id: 'main-001',
      name: 'Main Trading Account',
      accountNumber: 'ZTX-789456123',
      balance: 125847.32,
      equity: 128450.15,
      freeMargin: 95230.45,
      credit: 0,
      currency: 'USD',
      status: 'active',
      lastUpdated: new Date()
    },
    {
      id: 'demo-001',
      name: 'Demo Account',
      accountNumber: 'ZTX-DEMO-001',
      balance: 50000.00,
      equity: 52150.75,
      freeMargin: 48900.25,
      credit: 0,
      currency: 'USD',
      status: 'active',
      lastUpdated: new Date(Date.now() - 300000)
    },
    {
      id: 'retirement-001',
      name: 'Retirement Portfolio',
      accountNumber: 'ZTX-RET-456',
      balance: 245600.88,
      equity: 248900.22,
      freeMargin: 180450.33,
      credit: 5000,
      currency: 'USD',
      status: 'restricted',
      lastUpdated: new Date(Date.now() - 600000)
    }
  ]);

  // Mock transaction history data
  const [transactions] = useState([
    {
      id: 'TXN-001',
      type: 'deposit',
      description: 'Bank transfer deposit',
      amount: 5000.00,
      currency: 'USD',
      status: 'completed',
      date: '2025-01-05T10:30:00Z',
      reference: 'DEP-20250105-001',
      account: 'Main Trading Account',
      paymentMethod: 'Bank Transfer',
      paymentDetails: 'Chase Bank ****1234',
      fee: 0
    },
    {
      id: 'TXN-002',
      type: 'withdrawal',
      description: 'Profit withdrawal',
      amount: -2500.00,
      currency: 'USD',
      status: 'completed',
      date: '2025-01-04T14:15:00Z',
      reference: 'WTH-20250104-001',
      account: 'Main Trading Account',
      paymentMethod: 'Wire Transfer',
      paymentDetails: 'Bank of America ****5678',
      fee: 25.00
    },
    {
      id: 'TXN-003',
      type: 'transfer',
      description: 'Internal account transfer',
      amount: -1000.00,
      currency: 'USD',
      status: 'completed',
      date: '2025-01-03T09:45:00Z',
      reference: 'TRF-20250103-001',
      account: 'Main Trading Account',
      fee: 0
    },
    {
      id: 'TXN-004',
      type: 'commission',
      description: 'Trading commission - EURUSD',
      amount: -15.50,
      currency: 'USD',
      status: 'completed',
      date: '2025-01-03T16:22:00Z',
      reference: 'COM-20250103-001',
      account: 'Main Trading Account',
      fee: 0
    },
    {
      id: 'TXN-005',
      type: 'swap',
      description: 'Overnight swap - GBPJPY',
      amount: -8.75,
      currency: 'USD',
      status: 'completed',
      date: '2025-01-02T22:00:00Z',
      reference: 'SWP-20250102-001',
      account: 'Main Trading Account',
      fee: 0
    },
    {
      id: 'TXN-006',
      type: 'deposit',
      description: 'Credit card deposit',
      amount: 1500.00,
      currency: 'USD',
      status: 'failed',
      date: '2025-01-02T11:30:00Z',
      reference: 'DEP-20250102-001',
      account: 'Main Trading Account',
      paymentMethod: 'Credit Card',
      paymentDetails: 'Visa ****9876',
      fee: 0
    }
  ]);

  // Mock pending transactions data
  const [pendingTransactions] = useState([
    {
      id: 'PEND-001',
      type: 'withdrawal',
      description: 'Wire transfer withdrawal',
      amount: 3500.00,
      currency: 'USD',
      submittedAt: '2025-01-06T08:30:00Z',
      reference: 'WTH-20250106-001',
      progress: 65,
      cancellable: true
    },
    {
      id: 'PEND-002',
      type: 'deposit',
      description: 'Bank transfer deposit',
      amount: 2000.00,
      currency: 'USD',
      submittedAt: '2025-01-06T12:15:00Z',
      reference: 'DEP-20250106-002',
      progress: 25,
      cancellable: true
    }
  ]);

  // Mock KYC data
  const [kycData] = useState({
    overallStatus: 'partial',
    documents: [
      {
        type: 'identity',
        name: 'Government ID',
        description: 'Driver\'s license, passport, or national ID card',
        status: 'verified',
        verifiedAt: '2024-12-15T10:30:00Z'
      },
      {
        type: 'address',
        name: 'Proof of Address',
        description: 'Utility bill, bank statement, or lease agreement (within 3 months)',
        status: 'pending',
        submittedAt: '2025-01-05T14:20:00Z'
      },
      {
        type: 'income',
        name: 'Proof of Income',
        description: 'Pay stub, tax return, or employment letter',
        status: 'required'
      },
      {
        type: 'bank_statement',
        name: 'Bank Statement',
        description: 'Recent bank statement showing account ownership',
        status: 'rejected',
        rejectionReason: 'Document is older than 3 months. Please upload a more recent statement.'
      }
    ]
  });

  // Mock payment methods data
  const [paymentMethods] = useState([
    {
      id: 'PM-001',
      type: 'credit_card',
      details: {
        brand: 'Visa',
        number: '4532123456789012',
        expiryMonth: '12',
        expiryYear: '2027'
      },
      nickname: 'Personal Visa',
      isDefault: true,
      verified: true,
      encrypted: true,
      addedAt: '2024-11-15T10:30:00Z',
      limits: {
        daily: 10000,
        monthly: 50000
      }
    },
    {
      id: 'PM-002',
      type: 'bank_account',
      details: {
        bankName: 'Chase Bank',
        accountNumber: '1234567890',
        routingNumber: '021000021'
      },
      nickname: 'Main Checking',
      isDefault: false,
      verified: true,
      encrypted: true,
      addedAt: '2024-10-20T14:15:00Z',
      limits: {
        daily: 25000,
        monthly: 100000
      }
    },
    {
      id: 'PM-003',
      type: 'paypal',
      details: {
        email: 'trader@example.com'
      },
      nickname: 'PayPal Account',
      isDefault: false,
      verified: true,
      encrypted: true,
      addedAt: '2024-12-01T09:45:00Z',
      limits: {
        daily: 5000,
        monthly: 20000
      }
    }
  ]);

  // Set initial selected account
  useEffect(() => {
    if (accounts?.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts?.[0]);
    }
  }, [accounts, selectedAccount]);

  const handleAccountChange = (account) => {
    setSelectedAccount(account);
  };

  const handleTransfer = (transferData) => {
    console.log('Transfer funds:', transferData);
    // In a real app, this would make an API call
  };

  const handleExportTransactions = () => {
    console.log('Exporting transaction history...');
    // In a real app, this would generate and download a CSV/PDF
  };

  const handleCancelTransaction = (transactionId) => {
    console.log('Cancelling transaction:', transactionId);
    // In a real app, this would make an API call to cancel the transaction
  };

  const handleDocumentUpload = (documentType, file) => {
    console.log('Uploading document:', documentType, file?.name);
    // In a real app, this would upload the file to a secure server
  };

  const handleAddPaymentMethod = (methodData) => {
    console.log('Adding payment method:', methodData);
    // In a real app, this would redirect to a secure payment processor
  };

  const handleRemovePaymentMethod = (methodId) => {
    console.log('Removing payment method:', methodId);
    // In a real app, this would make an API call to remove the method
  };

  const handleSetDefaultPaymentMethod = (methodId) => {
    console.log('Setting default payment method:', methodId);
    // In a real app, this would make an API call to update the default
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const getTotalBalance = () => {
    return accounts?.reduce((total, account) => total + account?.balance, 0);
  };

  const getTotalEquity = () => {
    return accounts?.reduce((total, account) => total + account?.equity, 0);
  };

  const getPendingCount = () => {
    return pendingTransactions?.length;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'Receipt' },
    { id: 'kyc', label: 'Verification', icon: 'Shield' },
    { id: 'payments', label: 'Payment Methods', icon: 'CreditCard' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Account Management - Zonex Trader</title>
        <meta name="description" content="Manage your trading accounts, transactions, KYC verification, and payment methods" />
      </Helmet>
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} lg:pt-16 pt-16 pb-20 lg:pb-8`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
                <p className="text-gray-600 mt-1">Manage your accounts, transactions, and verification status</p>
              </div>
              <div className="hidden md:flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{formatCurrency(getTotalBalance())}</div>
                  <div className="text-gray-600">Total Balance</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{formatCurrency(getTotalEquity())}</div>
                  <div className="text-gray-600">Total Equity</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{getPendingCount()}</div>
                  <div className="text-gray-600">Pending</div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Summary Cards */}
          <div className="md:hidden grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
              <div className="text-lg font-bold text-gray-900">{formatCurrency(getTotalBalance())}</div>
              <div className="text-xs text-gray-600">Total Balance</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
              <div className="text-lg font-bold text-gray-900">{formatCurrency(getTotalEquity())}</div>
              <div className="text-xs text-gray-600">Total Equity</div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
              <div className="text-lg font-bold text-amber-600">{getPendingCount()}</div>
              <div className="text-xs text-gray-600">Pending</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2">
                  <AccountSwitcher
                    accounts={accounts}
                    selectedAccount={selectedAccount}
                    onAccountChange={handleAccountChange}
                    onTransfer={handleTransfer}
                  />
                </div>
                <div>
                  <PendingTransactions
                    pendingTransactions={pendingTransactions}
                    onCancel={handleCancelTransaction}
                  />
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <TransactionHistory
                transactions={transactions}
                onExport={handleExportTransactions}
              />
            )}

            {activeTab === 'kyc' && (
              <KYCStatus
                kycData={kycData}
                onDocumentUpload={handleDocumentUpload}
              />
            )}

            {activeTab === 'payments' && (
              <PaymentMethods
                paymentMethods={paymentMethods}
                onAddMethod={handleAddPaymentMethod}
                onRemoveMethod={handleRemovePaymentMethod}
                onSetDefault={handleSetDefaultPaymentMethod}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountManagement;